import React from 'react'
import {
  List,
  Datagrid,
  TextField,
  DateField,
  DeleteButton,
  TopToolbar,
  CreateButton,
  FunctionField,
  useTranslate,
  useNotify,
  useRefresh,
  useDataProvider,
} from 'react-admin'
import {
  CalculatorIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import { Chip, IconButton, Tooltip } from '@mui/material'

const MLTaskStatusChip = ({ record }) => {
  if (!record) return null

  const colors = {
    uploaded: 'default',
    processing: 'primary',
    completed: 'success',
    failed: 'error',
  }

  return (
    <Chip
      label={record.status.toUpperCase()}
      color={colors[record.status] || 'default'}
      size="small"
      variant="outlined"
    />
  )
}

const DownloadButton = ({ record }) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()

  if (!record || record.status !== 'completed' || !record.predictions_s3_key)
    return null

  const handleDownload = async (e) => {
    e.stopPropagation()
    try {
      // Fetch through the authenticated API (a bare window.open sends no token)
      const blob = await dataProvider.downloadMLTaskPredictions(record.id)
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download =
        record.predictions_filename || `predictions_${record.id}.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(objectUrl)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      notify(`Download failed: ${message}`, { type: 'error' })
    }
  }

  return (
    <Tooltip title="Download Predictions CSV">
      <IconButton onClick={handleDownload} size="small" color="primary">
        <ArrowDownTrayIcon className="h-5 w-5" />
      </IconButton>
    </Tooltip>
  )
}

const MLTaskListActions = () => (
  <TopToolbar>
    <CreateButton
      label="Run Predictions"
      variant="contained"
      sx={{ marginRight: 2 }}
    />
  </TopToolbar>
)

export const MLTaskList = () => {
  const translate = useTranslate()

  return (
    <List
      actions={<MLTaskListActions />}
      sort={{ field: 'created_at', order: 'DESC' }}
      resource="ml-tasks"
      title="Machine Learning Predictions"
    >
      <Datagrid
        sx={{
          '& .MuiTableCell-head': {
            fontWeight: 'bold',
            backgroundColor: '#f9f8f7',
          },
        }}
      >
        <TextField source="id" />
        <TextField source="filename" label="Source Spreadsheet" />
        <TextField source="model_name" label="Model" />
        <TextField source="ml_provider" label="Provider" />
        <FunctionField
          label="Status"
          render={(record) => <MLTaskStatusChip record={record} />}
        />
        <DateField source="created_at" label="Requested" showTime />
        <DateField source="completed_at" label="Finished" showTime />
        <FunctionField
          label="Actions"
          render={(record) => (
            <div className="flex items-center space-x-2">
              <DownloadButton record={record} />
              <DeleteButton />
            </div>
          )}
        />
      </Datagrid>
    </List>
  )
}

export const MLIcon = CalculatorIcon
